import { useCallback, useContext, useEffect, useMemo } from 'react';
import { ColorValues, Flex, Typography } from '@banhala/ui-kit';
import { bridge, localizeNumber } from 'shared/src/utils';
import type { GoodsOption } from 'shared/src/api';
import type { DeliveryType } from 'shared/src/api/goodsService/getGoodsOptions';
import { useIsAnonymousUser } from 'shared/src/hooks';

import { CouponStateTag, type CouponState } from '../CouponStateTag';
import { MaxDiscountPreviewRow } from '../MaxDiscountPreviewRow';
import { IconBrandIconThirdPL } from '../../../IconBrandIconThirdPL';
import { useGoodsOptionsPricing } from '../../queries/useGoodsOptionsPricing';
import { usePostBatchRedeemCoupon } from '../../queries/usePostBatchRedeemCoupon';
import { useLogDownloadCoupon } from '../../hooks/useLogCompleteCouponDownload';
import { useGetInlineOrderDeadlineText } from '../../hooks/useGetOrderDeadlineText';
import type { ProductOptionMaxDiscountMode } from '../../../../hooks/useProductOptionMaxDiscountMode';
import { GoodsParamsContext } from '../../../../context/GoodsParamsContext';
import { InlineTodayDeliveryIcon } from './InlineTodayDeliveryIcon';

const InlineExpectedPriceRow = ({
  finalPrice,
  disabled,
  emphasizePink,
}: {
  finalPrice: number;
  disabled?: boolean;
  emphasizePink?: boolean;
}) => {
  const labelColor = disabled ? 'content_tertiary' : 'content_primary';
  const priceColor = disabled
    ? 'content_tertiary'
    : emphasizePink
      ? 'content_pink_primary'
      : 'content_primary';
  return (
    <Flex.Row justifyContent={'space-between'} alignItems={'center'}>
      <Typography type={'subtitle1'} color={labelColor}>
        예상 결제금액
      </Typography>
      <Typography type={'h4'} color={priceColor}>
        {localizeNumber(finalPrice)}원
      </Typography>
    </Flex.Row>
  );
};

const InlineTodayInfoView = ({
  orderDeadline,
  deliveryType,
}: {
  orderDeadline: { text: string; isTimeOver: boolean };
  deliveryType: DeliveryType;
}) => {
  const isThirdPL = deliveryType === 'third_pl';
  const badgeBackgroundColor = isThirdPL
    ? orderDeadline.isTimeOver
      ? 'pink5'
      : 'bg_pink_primary'
    : orderDeadline.isTimeOver
      ? 'bg_lilac_primary'
      : 'lilac40';
  const badgeIconColor = isThirdPL
    ? orderDeadline.isTimeOver
      ? ColorValues.pink30
      : ColorValues.white
    : orderDeadline.isTimeOver
      ? ColorValues.lilac40
      : ColorValues.white;
  const textType = orderDeadline.isTimeOver ? 'body4' : 'subtitle4';
  const textRawColor = isThirdPL
    ? orderDeadline.isTimeOver
      ? undefined
      : ColorValues.pink30
    : orderDeadline.isTimeOver
      ? undefined
      : ColorValues.lilac40;

  return (
    <Flex.Row alignItems={'center'}>
      <Flex
        width={54}
        height={18}
        borderRadius={9}
        backgroundColor={badgeBackgroundColor}
        alignItems={'center'}
        justifyContent={'center'}
      >
        {isThirdPL ? (
          <IconBrandIconThirdPL width={41} height={11} color={badgeIconColor} />
        ) : (
          <InlineTodayDeliveryIcon color={badgeIconColor} />
        )}
      </Flex>
      <Flex margin={{ left: 4 }} flexShrink={1}>
        {textRawColor ? (
          <Typography type={textType} rawColor={textRawColor} numberOfLines={1}>
            {orderDeadline.text}
          </Typography>
        ) : (
          <Typography
            type={textType}
            color={'content_slate_primary'}
            numberOfLines={1}
          >
            {orderDeadline.text}
          </Typography>
        )}
      </Flex>
    </Flex.Row>
  );
};

export const InlinePriceSummary = ({
  selected,
  count,
  isAllSelected,
  isRestockMode,
  deliveryType,
  mode = 'disabled',
  isAutoRedeeming = false,
}: {
  selected: GoodsOption[];
  count: number;
  isAllSelected: boolean;
  isRestockMode: boolean;
  deliveryType: DeliveryType | undefined;
  mode?: ProductOptionMaxDiscountMode;
  isAutoRedeeming?: boolean;
}) => {
  const isAnonymousUser = useIsAnonymousUser();
  const getDeadlineText = useGetInlineOrderDeadlineText();
  const logCouponDownloadComplete = useLogDownloadCoupon();
  const injected = useContext(GoodsParamsContext);

  const pricingOptions = useMemo(
    () =>
      selected.map(option => ({
        sno: option.sno,
        count: option.purchase_count ?? 1,
      })),
    [selected],
  );
  const { data: pricing, isLoading: loadingPricing } = useGoodsOptionsPricing({
    options: pricingOptions,
  });
  const {
    mutateAsync: postBatchRedeemCoupon,
    isPending: loadingPostBatchRedeemCoupon,
  } = usePostBatchRedeemCoupon(selected);

  const isExperimental = mode !== 'disabled';

  const summary = useMemo(() => {
    const couponList = pricing?.coupon_list ?? [];
    const hasCoupons = couponList.length > 0;
    const allIssued = hasCoupons && couponList.every(c => c.item.is_issued);
    const totalDiscount = pricing?.discount_amount ?? 0;
    const couponState: CouponState = isAnonymousUser
      ? 'anonymous'
      : loadingPricing || loadingPostBatchRedeemCoupon || isAutoRedeeming
        ? 'loading'
        : !hasCoupons
          ? 'none'
          : allIssued
            ? 'issued'
            : 'downloadable';
    const unitPrice = selected[0]?.price ?? 0;
    const originalPrice = isAllSelected ? unitPrice * count : 0;
    const appliedDiscount = isExperimental
      ? totalDiscount
      : allIssued
        ? totalDiscount
        : 0;
    const finalPrice = Math.max(0, originalPrice - appliedDiscount);
    const restockPrice = unitPrice * count;

    return {
      allIssued,
      appliedDiscount,
      couponList,
      couponState,
      finalPrice,
      hasCoupons,
      originalPrice,
      restockPrice,
      totalDiscount,
    };
  }, [
    count,
    isAllSelected,
    isAnonymousUser,
    isExperimental,
    isAutoRedeeming,
    loadingPostBatchRedeemCoupon,
    loadingPricing,
    pricing,
    selected,
  ]);

  const {
    allIssued,
    appliedDiscount,
    couponList,
    couponState,
    finalPrice,
    hasCoupons,
    originalPrice,
    restockPrice,
    totalDiscount,
  } = summary;

  const showPreviewRow =
    isAllSelected &&
    !isRestockMode &&
    mode === 'variantB' &&
    hasCoupons &&
    !allIssued;
  const showItemRow = isAllSelected && !isRestockMode && hasCoupons;
  const showDiscountRow = showItemRow && !showPreviewRow;
  const showExpectedRow = isAllSelected && !showPreviewRow;

  const orderDeadline = deliveryType
    ? getDeadlineText({ delivery_type: deliveryType })
    : { text: '', isTimeOver: false };
  const showDeliveryInfo =
    isAllSelected &&
    !isRestockMode &&
    (deliveryType === 'shak' ||
      deliveryType === 'today' ||
      deliveryType === 'third_pl') &&
    orderDeadline.text.length > 0;

  const onPressRedeemCoupon = useCallback(async () => {
    if (isAnonymousUser) {
      bridge.assertLogin({
        message: '로그인을 해야 이용 할 수 있습니다.',
      });
      return;
    }

    if (!pricing || loadingPostBatchRedeemCoupon) return;
    const downloadableCoupons = pricing.coupon_list.filter(
      coupon => !coupon.item.is_issued,
    );

    if (downloadableCoupons.length === 0) return;

    try {
      await postBatchRedeemCoupon();
      downloadableCoupons.forEach(coupon => {
        logCouponDownloadComplete({ coupon, isDownloadAll: true });
      });
      bridge.toast('쿠폰이 발급되었습니다');
    } catch (error) {
      const ex = error as Error;
      bridge.toast(ex.message);
    }
  }, [
    isAnonymousUser,
    loadingPostBatchRedeemCoupon,
    logCouponDownloadComplete,
    postBatchRedeemCoupon,
    pricing,
  ]);

  useEffect(() => {
    if (loadingPricing || !pricing) return;
    if (pricing.coupon_list.length === 0) return;
    if (allIssued) return;

    bridge.logAnalyticsEvent({
      name: 'DISPLAY_COUPON',
      analytics: {
        ...pricing.coupon?.logging.analytics,
        SCREEN: injected?.logging ? injected.logging.screen : 'GOODS_DETAIL',
      },
    });
  }, [allIssued, injected, loadingPricing, pricing]);

  return (
    <Flex
      padding={{ top: 16, bottom: showPreviewRow ? 0 : 16 }}
      backgroundColor={'bg_always_white'}
    >
      {showDeliveryInfo && deliveryType && (
        <Flex padding={{ horizontal: 16 }}>
          <InlineTodayInfoView
            orderDeadline={orderDeadline}
            deliveryType={deliveryType}
          />
        </Flex>
      )}
      {showItemRow && (
        <Flex
          padding={{ horizontal: 16 }}
          margin={{ top: showDeliveryInfo ? 16 : 0 }}
          gap={8}
        >
          <Flex.Row justifyContent={'space-between'} alignItems={'center'}>
            <Typography type={'body2'} color={'content_tertiary'}>
              총 {count}개 상품
            </Typography>
            <Typography
              type={showPreviewRow ? 'h4' : 'body2'}
              color={'content_primary'}
            >
              {localizeNumber(originalPrice)}원
            </Typography>
          </Flex.Row>
          {showDiscountRow && (
            <Flex.Row justifyContent={'space-between'} alignItems={'center'}>
              <Typography type={'body2'} color={'content_tertiary'}>
                쿠폰 할인
              </Typography>
              <CouponStateTag
                state={couponState}
                couponCount={couponList.length}
                discount={totalDiscount}
                onPressDownload={onPressRedeemCoupon}
                mode={mode}
              />
            </Flex.Row>
          )}
        </Flex>
      )}
      {showPreviewRow && (
        <Flex
          margin={{
            top: showItemRow ? 12 : showDeliveryInfo ? 16 : 0,
          }}
        >
          <MaxDiscountPreviewRow
            potentialPrice={Math.max(0, originalPrice - totalDiscount)}
            loading={loadingPostBatchRedeemCoupon}
            onPressRedeem={onPressRedeemCoupon}
          />
        </Flex>
      )}
      {showDiscountRow && (
        <Flex
          padding={{ horizontal: 16 }}
          margin={{ top: 12 }}
          height={1}
          backgroundColor={'bg_tertiary'}
        />
      )}
      {showExpectedRow && (
        <Flex
          padding={{ horizontal: 16 }}
          margin={{
            top: showDiscountRow ? 12 : showDeliveryInfo ? 16 : 0,
          }}
        >
          <InlineExpectedPriceRow
            finalPrice={isRestockMode ? restockPrice : finalPrice}
            disabled={isRestockMode}
            emphasizePink={isExperimental && appliedDiscount > 0}
          />
        </Flex>
      )}
    </Flex>
  );
};
